import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { TagService } from '../../../../shared/services/apis/produto/tag.service';
import { TagStruct } from '../../../../shared/services/structs/produto/tag.struct';
import { UtilService } from '../../../../shared/services/util.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-tags',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-tags.component.html',
  styleUrl: './config-tags.component.css',
})
export class ConfigTagsComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<TagStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'actions'];

  public selection = new SelectionModel<TagStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private tagService: TagService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarTags();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/tags/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarTags(reset: boolean = false): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';

    this.tagService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar tags',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._dataSource.data = response.listaTags;
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaTags];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar tags', AlertType.error);
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarTags();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarTags(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: TagStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const title = isBulk ? 'Confirmar exclusão' : 'Confirmar exclusão';
    const description =
      'As tags selecionadas serão excluídas e automaticamente removidas dos produtos associados! Confirma exclusão das tags selecionadas?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idTag);
          this.tagService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir itens: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show('Sucesso', 'Grupos excluídos com sucesso', AlertType.success);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir grupos',
                AlertType.error
              );
            },
          });
        } else {
          this.tagService.delete(item!.idTag).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir grupo',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Grupo excluído com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir grupo',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
